import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { planTemplatesAPI } from '../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
  ArrowsUpDownIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { Button, Modal, Table, Card, Input, PermissionGate, ConfirmModal } from '../components/ui';
import { PageHeader, DataToolbar, StatusBadge, ActionIconButton, DataTableCard } from '../components/ui';
import {
  PlanTemplateFiltersSheet,
  PlanTemplateMobileCard,
  PlanTemplateMobileDetail,
  matchesFamilyRange,
  matchesPriceRange,
  PLAN_TYPE_CONFIG,
  type PlanFamilyRange,
  type PlanPriceRange,
  type PlanSortField,
  type PlanStatusFilter,
} from '../components/plan-templates';
import { TableColumn } from '../types';
import toast from 'react-hot-toast';

interface PlanTemplate {
  id: number;
  name: string;
  description?: string;
  planType: string;
  basePrice: number;
  features: string[];
  maxFamilyMembers: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PlanTemplateFormData {
  name: string;
  description: string;
  planType: string;
  basePrice: number;
  maxFamilyMembers: number;
  features: string[];
  isActive: boolean;
}

const PlanTemplateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  planTemplate?: PlanTemplate | null;
  onSave: (data: PlanTemplateFormData) => void;
}> = ({ isOpen, onClose, planTemplate, onSave }) => {
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [newFeature, setNewFeature] = useState('');
  const [featureError, setFeatureError] = useState('');

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<PlanTemplateFormData>({
    defaultValues: planTemplate ? {
      name: planTemplate.name,
      description: planTemplate.description || '',
      planType: planTemplate.planType,
      basePrice: planTemplate.basePrice,
      maxFamilyMembers: planTemplate.maxFamilyMembers,
      features: planTemplate.features,
      isActive: planTemplate.isActive
    } : {
      name: '',
      description: '',
      planType: 'funeral',
      basePrice: 0,
      maxFamilyMembers: 8,
      features: [],
      isActive: true
    }
  });

  const watchedFeatures = watch('features') || [];

  const onSubmit = (data: PlanTemplateFormData) => {
    onSave(data);
    reset();
    onClose();
  };

  const openFeatureModal = () => {
    setNewFeature('');
    setFeatureError('');
    setIsFeatureModalOpen(true);
  };

  const closeFeatureModal = () => {
    setIsFeatureModalOpen(false);
    setNewFeature('');
    setFeatureError('');
  };

  const confirmAddFeature = () => {
    const trimmed = newFeature.trim();
    if (!trimmed) {
      setFeatureError('La característica es requerida');
      return;
    }
    if (watchedFeatures.some((feature) => feature.toLowerCase() === trimmed.toLowerCase())) {
      setFeatureError('Esta característica ya está agregada');
      return;
    }
    reset({ ...watch(), features: [...watchedFeatures, trimmed] });
    closeFeatureModal();
  };

  const removeFeature = (index: number) => {
    const updatedFeatures = watchedFeatures.filter((_, i) => i !== index);
    reset({ ...watch(), features: updatedFeatures });
  };

  React.useEffect(() => {
    if (isOpen) {
      reset(planTemplate ? {
        name: planTemplate.name,
        description: planTemplate.description || '',
        planType: planTemplate.planType,
        basePrice: planTemplate.basePrice,
        maxFamilyMembers: planTemplate.maxFamilyMembers,
        features: planTemplate.features,
        isActive: planTemplate.isActive
      } : {
        name: '',
        description: '',
        planType: 'funeral',
        basePrice: 0,
        maxFamilyMembers: 8,
        features: [],
        isActive: true
      });
      closeFeatureModal();
    }
  }, [isOpen, planTemplate, reset]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (isFeatureModalOpen) return;
          onClose();
        }}
        title={planTemplate ? 'Editar Plantilla de Plan' : 'Crear Plantilla de Plan'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Nombre del Plan"
                placeholder="Ej: Plan Básico, Plan VIP"
                error={errors.name?.message}
                required
                {...register('name', { 
                  required: 'El nombre es requerido',
                  minLength: { value: 2, message: 'El nombre debe tener al menos 2 caracteres' }
                })}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Descripción del plan..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Plan
              </label>
              <select
                {...register('planType')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="funeral">Funeral</option>
                <option value="cremation">Cremación</option>
                <option value="burial">Entierro</option>
                <option value="memorial">Memorial</option>
              </select>
            </div>

            <div>
              <Input
                label="Precio Base"
                type="number"
                step="0.01"
                placeholder="0.00"
                error={errors.basePrice?.message}
                required
                {...register('basePrice', { 
                  required: 'El precio base es requerido',
                  min: { value: 0, message: 'El precio debe ser mayor a 0' }
                })}
              />
            </div>

            <div>
              <Input
                label="Máximo de Familiares"
                type="number"
                placeholder="8"
                error={errors.maxFamilyMembers?.message}
                required
                {...register('maxFamilyMembers', { 
                  required: 'El máximo de familiares es requerido',
                  min: { value: 1, message: 'Debe ser al menos 1' },
                  max: { value: 20, message: 'Máximo 20 familiares' }
                })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Características
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={openFeatureModal}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  Agregar
                </Button>
              </div>
              <div className="space-y-2">
                {watchedFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 flex-1">{feature}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFeature(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {watchedFeatures.length === 0 && (
                  <p className="text-sm text-gray-500">No hay características agregadas</p>
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Plan activo</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="danger">
              {planTemplate ? 'Actualizar Plan' : 'Crear Plan'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isFeatureModalOpen}
        onClose={closeFeatureModal}
        title="Agregar característica"
        size="sm"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmAddFeature();
          }}
          className="space-y-4"
        >
          <Input
            label="Característica"
            placeholder="Ej: Traslado incluido, Velación 24h"
            value={newFeature}
            onChange={(value) => {
              setNewFeature(value);
              if (featureError) setFeatureError('');
            }}
            error={featureError}
            required
          />
          <div className="flex justify-end space-x-3 pt-2">
            <Button type="button" variant="outline" onClick={closeFeatureModal}>
              Cancelar
            </Button>
            <Button type="submit">
              Agregar
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

const PlanTemplates: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlanTemplate, setSelectedPlanTemplate] = useState<PlanTemplate | null>(null);
  const [detailTemplate, setDetailTemplate] = useState<PlanTemplate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlanStatusFilter>('all');
  const [priceRange, setPriceRange] = useState<PlanPriceRange>('all');
  const [familyRange, setFamilyRange] = useState<PlanFamilyRange>('all');
  const [sortField, setSortField] = useState<PlanSortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {}
  });
  const queryClient = useQueryClient();

  const { data: planTemplates, isLoading } = useQuery<{ data: { data: PlanTemplate[] } }>(
    'planTemplates',
    () => planTemplatesAPI.getAll()
  );

  const allTemplates = planTemplates?.data?.data ?? [];

  const createMutation = useMutation(planTemplatesAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('planTemplates');
      toast.success('Plantilla de plan creada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear la plantilla de plan');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: PlanTemplateFormData }) => planTemplatesAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('planTemplates');
        toast.success('Plantilla de plan actualizada exitosamente');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Error al actualizar la plantilla de plan');
      }
    }
  );

  const deleteMutation = useMutation(planTemplatesAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('planTemplates');
      toast.success('Plantilla de plan eliminada exitosamente');
      setDetailTemplate(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar la plantilla de plan');
    }
  });

  const handleEdit = (planTemplate: PlanTemplate) => {
    setSelectedPlanTemplate(planTemplate);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedPlanTemplate(null);
    setIsModalOpen(true);
  };

  const handleSave = (data: PlanTemplateFormData) => {
    if (selectedPlanTemplate) {
      updateMutation.mutate({ id: selectedPlanTemplate.id.toString(), data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Plantilla de Plan',
      message: '¿Está seguro de que desea eliminar esta plantilla de plan? Esta acción no se puede deshacer.',
      type: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(id.toString());
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const clearAllFilters = () => {
    setTypeFilter('');
    setStatusFilter('all');
    setPriceRange('all');
    setFamilyRange('all');
    setSortField('name');
    setSortDir('asc');
  };

  const hasActiveFilters =
    Boolean(typeFilter) ||
    statusFilter !== 'all' ||
    priceRange !== 'all' ||
    familyRange !== 'all' ||
    sortField !== 'name' ||
    sortDir !== 'asc';

  const filteredPlanTemplates = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = allTemplates.filter((template) => {
      const matchesSearch =
        !q ||
        template.name.toLowerCase().includes(q) ||
        template.description?.toLowerCase().includes(q) ||
        template.planType.toLowerCase().includes(q) ||
        (PLAN_TYPE_CONFIG[template.planType]?.label || '').toLowerCase().includes(q);

      if (!matchesSearch) return false;
      if (typeFilter && template.planType !== typeFilter) return false;
      if (statusFilter === 'active' && !template.isActive) return false;
      if (statusFilter === 'inactive' && template.isActive) return false;
      if (!matchesPriceRange(Number(template.basePrice || 0), priceRange)) return false;
      if (!matchesFamilyRange(Number(template.maxFamilyMembers || 0), familyRange)) return false;
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sortField) {
        case 'name':
          return a.name.localeCompare(b.name, 'es') * dir;
        case 'price':
          return (Number(a.basePrice) - Number(b.basePrice)) * dir;
        case 'family':
          return (Number(a.maxFamilyMembers) - Number(b.maxFamilyMembers)) * dir;
        case 'date':
          return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
        default: {
          const _exhaustive: never = sortField;
          return _exhaustive;
        }
      }
    });

    return list;
  }, [
    allTemplates,
    searchTerm,
    typeFilter,
    statusFilter,
    priceRange,
    familyRange,
    sortField,
    sortDir,
  ]);

  const activeCount = allTemplates.filter((p) => p.isActive).length;
  const avgPrice =
    allTemplates.reduce((sum, p) => sum + Number(p.basePrice || 0), 0) /
    Math.max(1, allTemplates.length);

  const columns: TableColumn<PlanTemplate>[] = [
    {
      key: 'name',
      label: 'Nombre',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'planType',
      label: 'Tipo',
      render: (value) => {
        const config = PLAN_TYPE_CONFIG[value] || PLAN_TYPE_CONFIG.default;
        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.className}`}>
            {config.label}
          </span>
        );
      }
    },
    {
      key: 'basePrice',
      label: 'Precio Base',
      render: (value) => (
        <span className="text-sm font-semibold text-slate-800">
          ${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
        </span>
      ),
      sortable: true
    },
    {
      key: 'maxFamilyMembers',
      label: 'Max. Familiares',
      sortable: true,
      render: (value) => (
        <span className="text-sm text-gray-500">{value} personas</span>
      )
    },
    {
      key: 'features',
      label: 'Características',
      render: (features) => (
        <span className="text-sm text-gray-500">
          {features.length} items
        </span>
      )
    },
    {
      key: 'isActive',
      label: 'Estado',
      render: (value) => (
        <StatusBadge tone={value ? 'success' : 'danger'}>{value ? 'Activo' : 'Inactivo'}</StatusBadge>
      )
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, template) => (
        <div className="flex justify-end space-x-1">
          <PermissionGate permission="plan_templates.update">
            <ActionIconButton title="Editar" onClick={() => handleEdit(template)}>
              <PencilIcon className="h-4 w-4" />
            </ActionIconButton>
          </PermissionGate>
          <PermissionGate permission="plan_templates.delete">
            <ActionIconButton title="Eliminar" onClick={() => handleDelete(template.id)} tone="danger">
              <TrashIcon className="h-4 w-4" />
            </ActionIconButton>
          </PermissionGate>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4 animate-fade-in-up py-4 pb-8 md:space-y-6 md:py-6">
      <div className="hidden md:block">
        <PageHeader
          title="Plantillas de Planes"
          subtitle="Defina ofertas funerarias con estructura, precios y cobertura familiar."
          actions={
            <PermissionGate permission="plan_templates.create">
              <Button onClick={handleCreate} className="bg-slate-800 hover:bg-slate-900 text-white shadow-sm">
                <PlusIcon className="h-4 w-4 mr-2" />
                Crear Plantilla
              </Button>
            </PermissionGate>
          }
        />
      </div>

      <div className="hidden gap-4 md:grid md:grid-cols-3">
        <Card className="border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Total</p>
          <p className="text-2xl font-semibold text-slate-900">{allTemplates.length}</p>
        </Card>
        <Card className="border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Activos</p>
          <p className="text-2xl font-semibold text-slate-900">{activeCount}</p>
        </Card>
        <Card className="border border-slate-200 p-5">
          <p className="text-sm text-slate-500">Precio promedio</p>
          <p className="text-2xl font-semibold text-slate-900">
            ${avgPrice.toLocaleString('es-MX', { maximumFractionDigits: 0 })}
          </p>
        </Card>
      </div>

      {/* Mobile detail */}
      {detailTemplate ? (
        <PlanTemplateMobileDetail
          template={detailTemplate}
          onBack={() => setDetailTemplate(null)}
          onEdit={() => handleEdit(detailTemplate)}
          onDelete={() => handleDelete(detailTemplate.id)}
        />
      ) : (
        <div className="space-y-4 md:hidden">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Plantillas de Planes</h1>
            <p className="mt-1 text-sm text-slate-500">
              Defina ofertas funerarias con estructura, precios y cobertura familiar.
            </p>
          </div>

          <PermissionGate permission="plan_templates.create">
            <Button
              onClick={handleCreate}
              className="flex h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
            >
              <PlusIcon className="mr-2 h-5 w-5" />
              Crear Plantilla
            </Button>
          </PermissionGate>

          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-primary-50">
                <DocumentTextIcon className="h-4 w-4 text-primary-600" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
              <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : allTemplates.length}
              </p>
            </div>
            <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50">
                <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Activas</p>
              <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : activeCount}
              </p>
            </div>
            <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-sky-50">
                <BanknotesIcon className="h-4 w-4 text-sky-600" />
              </div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Promedio</p>
              <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : `$${avgPrice.toLocaleString('es-MX', { maximumFractionDigits: 0 })}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o tipo..."
                className="h-11 w-full rounded-2xl border border-[#E5EAF1] bg-white pl-10 pr-10 text-sm font-medium text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                type="search"
                aria-label="Buscar plantillas"
              />
              {searchTerm ? (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Limpiar búsqueda"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setFiltersSheetOpen(true)}
              className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E5EAF1] bg-primary-50 text-primary-700 transition active:scale-95"
              aria-label="Abrir filtros"
            >
              <FunnelIcon className="h-5 w-5" />
              {hasActiveFilters ? (
                <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-primary-50" />
              ) : null}
            </button>
          </div>

          <div className="flex items-center justify-between border-b border-[#E5EAF1] pb-2">
            <p className="text-sm font-medium text-slate-600">
              <span className="tabular-nums font-semibold text-slate-900">
                {filteredPlanTemplates.length}
              </span>{' '}
              resultados
            </p>
            <button
              type="button"
              onClick={() => setFiltersSheetOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 active:scale-95"
            >
              <ArrowsUpDownIcon className="h-4 w-4" />
              Ordenar
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-[120px] animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : filteredPlanTemplates.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-14 text-center">
              <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-800">Sin plantillas</p>
              <p className="mt-1 text-xs text-slate-500">
                Ajuste la búsqueda o cree una nueva plantilla.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredPlanTemplates.map((template) => (
                <PlanTemplateMobileCard
                  key={template.id}
                  template={template}
                  onOpenDetail={() => setDetailTemplate(template)}
                  onEdit={() => handleEdit(template)}
                  onDelete={() => handleDelete(template.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="hidden md:block">
        <DataTableCard>
          <div className="border-b border-slate-200 p-4">
            <DataToolbar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Buscar por nombre, descripción o tipo..."
              rightSlot={
                <span className="text-sm font-medium text-slate-500">
                  {filteredPlanTemplates.length} resultados
                </span>
              }
            />
          </div>

          <div className="overflow-x-auto">
            <Table
              data={filteredPlanTemplates}
              columns={columns}
              loading={isLoading}
              emptyMessage={
                <div className="py-12 text-center">
                  <p className="text-sm text-gray-500">No se encontraron plantillas</p>
                </div>
              }
              className="min-w-full divide-y divide-slate-100"
              rowClassName="hover:bg-slate-50"
            />
          </div>
        </DataTableCard>
      </div>

      <PlanTemplateFiltersSheet
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
        planType={typeFilter}
        onPlanTypeChange={setTypeFilter}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        priceRange={priceRange}
        onPriceRangeChange={setPriceRange}
        familyRange={familyRange}
        onFamilyRangeChange={setFamilyRange}
        sortField={sortField}
        sortDir={sortDir}
        onSortFieldChange={setSortField}
        onSortDirChange={setSortDir}
        onClear={clearAllFilters}
      />

      <PlanTemplateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        planTemplate={selectedPlanTemplate}
        onSave={handleSave}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default PlanTemplates;

